/**
 * useSimilarSuggestions.js
 * Sugerencias de valores parecidos (pg_trgm) mientras se captura --
 * no bloquea, solo informa. Mismo patron de useUniqueValidation.js
 * (debounce + fetch + config 100% desde schema_form, zero hardcode).
 */
import { useState, useCallback, useRef } from 'react';
import { secureFetch } from '../utils/authInterceptor';

export function useSimilarSuggestions(componentForm) {
  const [suggestions, setSuggestions] = useState({}); // { fieldName: [{valor, display_extra, similitud}] }
  const [searching, setSearching] = useState({});
  const timeoutsRef = useRef({});

  const clearFieldTimeout = useCallback((fieldName) => {
    if (timeoutsRef.current[fieldName]) {
      clearTimeout(timeoutsRef.current[fieldName]);
      delete timeoutsRef.current[fieldName];
    }
  }, []);

  const clearSuggestions = useCallback((fieldName) => {
    setSuggestions(prev => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const search = useCallback((field, value) => {
    const config = field.similarSuggestions;
    if (!config) return;

    // Cadena muy corta -- el trigram de un prefijo contra nombres largos
    // no da resultados utiles (ya lo validamos con datos reales)
    if (!value || value.trim().length < 4) {
      clearSuggestions(field.name);
      return;
    }

    clearFieldTimeout(field.name);

    timeoutsRef.current[field.name] = setTimeout(async () => {
      setSearching(prev => ({ ...prev, [field.name]: true }));

      try {
        const token = sessionStorage.getItem('mapasac_access_token');
        const params = new URLSearchParams({
          table: config.table || componentForm,
          field: config.field || field.name,
          value,
          limit: config.limit || 5
        });
        if (config.displayField) params.set('displayField', config.displayField);

        const response = await secureFetch(`/api/sqlexec_generico/similar-suggestions?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await response.json();

        if (json.success) {
          setSuggestions(prev => ({ ...prev, [field.name]: json.data.suggestions || [] }));
        }
      } catch (error) {
        console.error(`Error buscando sugerencias en ${field.name}:`, error);
      } finally {
        setSearching(prev => {
          const next = { ...prev };
          delete next[field.name];
          return next;
        });
      }
    }, 500); // mismo debounce que useUniqueValidation
  }, [componentForm, clearFieldTimeout, clearSuggestions]);

  const reset = useCallback(() => {
    Object.keys(timeoutsRef.current).forEach(clearFieldTimeout);
    setSuggestions({});
    setSearching({});
  }, [clearFieldTimeout]);

  return { suggestions, searching, search, clearSuggestions, reset };
}
